import "./style.css";
import iconAccident from "../../views/MapView/icons/accident.svg";
import iconHazard from "../../views/MapView/icons/hazard.svg";
import iconJam from "../../views/MapView/icons/jam.svg";
import iconRoadClosed from "../../views/MapView/icons/road_closed.svg";

const LEVEL_HEX = {
  5: "#d71307", 4: "#ff7b02", 3: "#ffad03",
  2: "#ffcc01", 1: "#28a446", 0: "#999",
};

const LEVEL_LABEL = {
  5: "Colapso", 4: "Muy lento", 3: "Lento",
  2: "Moderado", 1: "Fluido", 0: "Desconocido",
};

// Simbología de diseño (line-art amarillo) para la ventana de detalle.
const ALERT_TYPE_GLYPH = {
  ACCIDENT: iconAccident, HAZARD: iconHazard, JAM: iconJam,
  ROAD_CLOSED: iconRoadClosed,
};
// Tipos sin glifo dedicado mantienen el emoji.
const ALERT_TYPE_EMOJI = {
  POLICE: "👮", WEATHERHAZARD: "🌧️",
};

const ALERT_TYPE_LABEL = {
  ACCIDENT: "Accidente",
  HAZARD: "Peligro",
  JAM: "Atasco reportado",
  ROAD_CLOSED: "Calle cerrada",
  POLICE: "Policía",
  WEATHERHAZARD: "Clima adverso",
};

const SUBTYPE_LABEL = {
  ACCIDENT_MAJOR: "Accidente grave",
  ACCIDENT_MINOR: "Accidente menor",
  JAM_HEAVY_TRAFFIC: "Tráfico pesado",
  JAM_MODERATE_TRAFFIC: "Tráfico moderado",
  JAM_LIGHT_TRAFFIC: "Tráfico ligero",
  JAM_STAND_STILL_TRAFFIC: "Tráfico detenido",
  HAZARD_ON_ROAD: "Peligro en la vía",
  HAZARD_ON_ROAD_CAR_STOPPED: "Vehículo detenido",
  HAZARD_ON_ROAD_CONSTRUCTION: "Construcción",
  HAZARD_ON_ROAD_OBJECT: "Objeto en la vía",
  HAZARD_ON_ROAD_POT_HOLE: "Bache",
  HAZARD_ON_ROAD_ROAD_KILL: "Animal atropellado",
  HAZARD_ON_ROAD_LANE_CLOSED: "Pista cerrada",
  HAZARD_ON_ROAD_TRAFFIC_LIGHT_FAULT: "Semáforo descompuesto",
  HAZARD_ON_SHOULDER: "Peligro en la berma",
  HAZARD_ON_SHOULDER_CAR_STOPPED: "Vehículo en berma",
  HAZARD_ON_SHOULDER_ANIMALS: "Animales en berma",
  HAZARD_ON_SHOULDER_MISSING_SIGN: "Falta señalización",
  HAZARD_WEATHER: "Condición climática",
  HAZARD_WEATHER_FOG: "Niebla",
  HAZARD_WEATHER_HAIL: "Granizo",
  HAZARD_WEATHER_FLOOD: "Inundación",
  HAZARD_WEATHER_FREEZING_RAIN: "Lluvia helada",
  HAZARD_WEATHER_HEAVY_RAIN: "Lluvia intensa",
  HAZARD_WEATHER_HEAVY_SNOW: "Nieve intensa",
  HAZARD_WEATHER_TORNADO: "Tornado",
  ROAD_CLOSED_HAZARD: "Cerrada por peligro",
  ROAD_CLOSED_CONSTRUCTION: "Cerrada por obra",
  ROAD_CLOSED_EVENT: "Cerrada por evento",
  POLICE_VISIBLE: "Policía visible",
  POLICE_HIDING: "Policía oculta",
};

const COUNTRY_LABEL = {
  CL: "Chile",
  CI: "Chile",   // los datos de Waze a veces traen "CI"
  AR: "Argentina",
  PE: "Perú",
  BO: "Bolivia",
};

const ROAD_TYPE_LABEL = {
  1: "Calle",
  2: "Calle principal",
  3: "Autopista",
  4: "Rampa",
  5: "Sendero",
  6: "Calle secundaria",
  7: "Camino rural",
  8: "Carretera",
  17: "Acceso privado",
  20: "Pista",
};

/** Convierte "14-25-05" → "14:25" (solo hora y minuto). */
const formatTick = (t) => {
  if (!t) return "—";
  const parts = t.split("-");
  if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
  return t;
};


const SidebarDetail = ({ feature, onClose }) => {
  if (!feature) return null;
  const p = feature.properties || {};
  const isAlert = feature.geometry?.type === "Point";

  return (
    <div className="sidebar-detail">
      <div className="sidebar-detail-header">
        <span className="sidebar-detail-icon">
          {(() => {
            const glyph = isAlert ? ALERT_TYPE_GLYPH[p.type] : iconJam;
            if (glyph) return <img src={glyph} alt="" className="sidebar-detail-glyph" />;
            return isAlert ? (ALERT_TYPE_EMOJI[p.type] || "📍") : "🚗";
          })()}
        </span>
        <div className="sidebar-detail-title">
          <div className="sidebar-detail-eyebrow">
            {isAlert ? ALERT_TYPE_LABEL[p.type] || p.type || "Alerta" : "Atasco"}
          </div>
          <div className="sidebar-detail-street" title={p.street || p.streetName || ""}>
            {p.street || p.streetName || "Calle sin nombre"}
          </div>
        </div>
        <button className="sidebar-detail-close" onClick={onClose}>×</button>
      </div>

      <div className="sidebar-detail-body">
        {isAlert ? (
          <AlertBody p={p} />
        ) : (
          <JamBody p={p} />
        )}
      </div>
    </div>
  );
};


const AlertBody = ({ p }) => (
  <>
    <Row
      label="Categoría"
      value={ALERT_TYPE_LABEL[p.type] || p.type || "—"}
    />
    {p.subtype && (
      <Row
        label="Detalle"
        value={SUBTYPE_LABEL[p.subtype] || prettifyConstant(p.subtype)}
      />
    )}
    {Number.isFinite(p.nThumbsUp) && (
      <Row
        label="Confirmaciones 👍"
        value={`${p.nThumbsUp}`}
        hint="usuarios que validaron el reporte"
      />
    )}
    {Number.isFinite(p.reliability) && (
      <Row
        label="Confiabilidad"
        value={`${p.reliability} / 10`}
        hint="estimación interna de Waze"
      />
    )}
    {Number.isFinite(p.reportRating) && (
      <Row label="Rating del reporte" value={`${p.reportRating}`} />
    )}
    <Row label="Fecha" value={p.date || "—"} />
    <Row label="Hora del reporte" value={formatTick(p.time)} />
    {p.country && (
      <Row
        label="País"
        value={COUNTRY_LABEL[p.country] || p.country}
      />
    )}
    {p.city && <Row label="Ciudad" value={p.city} />}
    {p.uuid && <UuidRow uuid={p.uuid} />}
  </>
);


const JamBody = ({ p }) => (
  <>
    <Row
      label="Nivel"
      value={
        <span className="sidebar-detail-level" style={{ background: LEVEL_HEX[p.level || 0] }}>
          {p.level ?? 0} · {LEVEL_LABEL[p.level || 0]}
        </span>
      }
    />
    {Number.isFinite(p.speedKMH) && (
      <Row label="Velocidad" value={`${Math.round(p.speedKMH)} km/h`} />
    )}
    {Number.isFinite(p.length) && (
      <Row label="Largo del atasco" value={`${Math.round(p.length)} m`} />
    )}
    {(p.street || p.streetName) && (
      <Row label="Calle" value={p.street || p.streetName} />
    )}
    {Number.isFinite(p.roadType) && (
      <Row
        label="Tipo de vía"
        value={ROAD_TYPE_LABEL[p.roadType] || `código ${p.roadType}`}
      />
    )}
    {Number.isFinite(p.delay) && (
      <Row label="Demora estimada" value={`${Math.round(p.delay / 60)} min`} />
    )}
    <Row label="Fecha" value={p.date || "—"} />
    <Row label="Hora del registro" value={formatTick(p.time)} />
    {p.uuid && <UuidRow uuid={p.uuid} />}
  </>
);


const UuidRow = ({ uuid }) => {
  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(uuid).catch(() => {});
  };
  const short = uuid.length > 13 ? `${uuid.slice(0, 8)}…${uuid.slice(-4)}` : uuid;
  return (
    <div className="sidebar-detail-row">
      <div className="sidebar-detail-row-label">ID</div>
      <div className="sidebar-detail-row-value small uuid-row" onClick={copy} title="Click para copiar">
        {short}
        <span className="copy-hint">copiar</span>
      </div>
    </div>
  );
};


const Row = ({ label, value, hint }) => (
  <div className="sidebar-detail-row">
    <div className="sidebar-detail-row-label">
      {label}
      {hint && <span className="sidebar-detail-hint" title={hint}> ⓘ</span>}
    </div>
    <div className="sidebar-detail-row-value">{value}</div>
  </div>
);


/** Convierte HAZARD_ON_ROAD_POT_HOLE → "Hazard on road pot hole" como fallback */
const prettifyConstant = (s) =>
  String(s).toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());


export default SidebarDetail;
